import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Card,
  Table,
  Button,
  Drawer,
  Form,
  Input,
  Select,
  Upload,
  message,
  Space,
  Typography,
  Tag,
  Popconfirm,
  Row,
  Col,
  Image,
  Tooltip,
  Divider
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  FileTextOutlined,
  VideoCameraOutlined,
  LinkOutlined,
  UploadOutlined,
  EyeOutlined,
  DownloadOutlined,
  ReloadOutlined
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import withRoleAccess from '../../hoc/withRoleAccess';
import Sidebar from '../../components/Sidebar/Sidebar';
import Header from '../../components/Header/Header';

const { Text, Title } = Typography;
const { Option } = Select;
const { TextArea } = Input;

// Helper function to extract video thumbnail
const getVideoThumbnail = (url) => {
  // YouTube
  const youtubeMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\n?#]+)/);
  if (youtubeMatch) {
    return `https://img.youtube.com/vi/${youtubeMatch[1]}/mqdefault.jpg`;
  }
  
  // Vimeo
  const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
  if (vimeoMatch) {
    return `https://vumbnail.com/${vimeoMatch[1]}.jpg`;
  }
  
  // Default video icon
  return null;
};

// Helper function to get file icon based on extension
const getFileIcon = (filename, resourceType) => {
  if (resourceType === 'video_link') {
    return <VideoCameraOutlined style={{ fontSize: '24px', color: '#ff4d4f' }} />;
  }
  if (resourceType === 'file_link') {
    return <LinkOutlined style={{ fontSize: '24px', color: '#1890ff' }} />;
  }
  if (resourceType === 'file_upload') {
    return <FileTextOutlined style={{ fontSize: '24px', color: '#52c41a' }} />;
  }
  return <FileTextOutlined style={{ fontSize: '24px', color: '#666' }} />;
};

const ResourceManagement = () => {
  const { t } = useTranslation();
  const [resources, setResources] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [editingResource, setEditingResource] = useState(null);
  const [form] = Form.useForm();
  const [searchText, setSearchText] = useState('');
  const [filterCategory, setFilterCategory] = useState(null);
  const [filterSubject, setFilterSubject] = useState(null);
  const [filterType, setFilterType] = useState(null);
  const [categoryDrawerVisible, setCategoryDrawerVisible] = useState(false);
  const [subjectDrawerVisible, setSubjectDrawerVisible] = useState(false);
  const [categoryForm] = Form.useForm();
  const [subjectForm] = Form.useForm();

  const API_URL = process.env.REACT_APP_API_URL;

  useEffect(() => {
    fetchResources();
    fetchCategories();
    fetchSubjects();
  }, []);

  const fetchResources = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/resources`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setResources(response.data);
    } catch (error) {
      console.error('Error fetching resources:', error);
      message.error(t('Failed to fetch resources'));
    }
    setLoading(false);
  };

const fetchCategories = async () => {
  try {
    const response = await axios.get(`${API_URL}/resources/categories`);
    setCategories(response.data);
  } catch (error) {
    console.error("❌ Error fetching categories:", error);
    if (error.response) {
      console.error("Response error (categories):", error.response.data);
    }
  }
};

const fetchSubjects = async (categoryId) => {
  try {
    const response = await axios.get(`${API_URL}/resources/subjects`);
    setSubjects(response.data);
  } catch (error) {
    console.error("❌ Error fetching subjects:", error);
    if (error.response) {
      console.error("Response error (subjects):", error.response.data);
    }
  }
};

  const handleCreateCategory = async (values) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/resources/categories`, values, {
        headers: { Authorization: `Bearer ${token}` }
      });
      message.success(t('Category created successfully'));
      setCategoryDrawerVisible(false);
      categoryForm.resetFields();
      fetchCategories();
    } catch (error) {
      console.error('Error creating category:', error);
      message.error(t('Failed to create category'));
    }
  };

  const handleCreateSubject = async (values) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/resources/subjects`, values, {
        headers: { Authorization: `Bearer ${token}` }
      });
      message.success(t('Subject created successfully'));
      setSubjectDrawerVisible(false);
      subjectForm.resetFields();
      fetchSubjects(values.category_id);
    } catch (error) {
      console.error('Error creating subject:', error);
      message.error(t('Failed to create subject'));
    }
  };

  const handleSubmit = async (values) => {
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      
      Object.keys(values).forEach(key => {
        if (values[key] !== undefined && values[key] !== null) {
          if (key === 'pdfFile' && values[key]?.fileList) {
            if (values[key].fileList[0]?.originFileObj) {
              formData.append('pdfFile', values[key].fileList[0].originFileObj);
            }
          } else {
            formData.append(key, values[key]);
          }
        }
      });

      const url = editingResource 
        ? `${API_URL}/resources/${editingResource.id}`
        : `${API_URL}/resources`;
      
      const method = editingResource ? 'put' : 'post';
      
      await axios[method](url, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      message.success(editingResource ? t('Resource updated successfully') : t('Resource created successfully'));
      setDrawerVisible(false);
      form.resetFields();
      setEditingResource(null);
      fetchResources();
    } catch (error) {
      console.error('Error saving resource:', error);
      message.error(t('Failed to save resource'));
    }
  };

  const handleDelete = async (id) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/resources/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      message.success(t('Resource deleted successfully'));
      fetchResources();
    } catch (error) {
      console.error('Error deleting resource:', error);
      message.error(t('Failed to delete resource'));
    }
  };

  const handleEdit = (resource) => {
    setEditingResource(resource);
    form.setFieldsValue({
      category: resource.category,
      subject: resource.subject,
      title: resource.title,
      description: resource.description,
      resource_type: resource.resource_type,
      link: resource.link
    });
    if (resource.category) {
      fetchSubjects(resource.category);
    }
    setDrawerVisible(true);
  };

  const handleCategoryChange = (categoryId) => {
    form.setFieldValue('subject', undefined);
    fetchSubjects(categoryId);
  };

  // Filter resources based on search and filters
  const filteredResources = resources.filter(resource => {
    const matchesSearch = !searchText || 
      resource.title?.toLowerCase().includes(searchText.toLowerCase()) ||
      resource.description?.toLowerCase().includes(searchText.toLowerCase());
    
    const matchesCategory = !filterCategory || resource.category_id === filterCategory;
    const matchesSubject = !filterSubject || resource.subject_id === filterSubject;
    const matchesType = !filterType || resource.resource_type === filterType;

    return matchesSearch && matchesCategory && matchesSubject && matchesType;
  });

  return (
    <div>
      <Sidebar />
      <Header />
      <div style={{ margin: '70px 20px 20px 90px', padding: '20px' }}>
        <Card>
          <div style={{ marginBottom: 24 }}>
            <Title level={2}>{t('Resource Management')}</Title>
            
            {/* Filters and Search */}
            <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
              <Col xs={24} sm={12} md={6}>
                <Input
                  placeholder={t('Search resources...')}
                  prefix={<SearchOutlined />}
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  allowClear
                />
              </Col>
              
              <Col xs={24} sm={12} md={4}>
                <Select
                  placeholder={t('Category')}
                  value={filterCategory}
                  onChange={setFilterCategory}
                  allowClear
                  style={{ width: '100%' }}
                >
                  {categories.map(cat => (
                    <Option key={cat.id} value={cat.id}>{cat.name}</Option>
                  ))}
                </Select>
              </Col>
              
              <Col xs={24} sm={12} md={4}>
                <Select
                  placeholder={t('Subject')}
                  value={filterSubject}
                  onChange={setFilterSubject}
                  allowClear
                  style={{ width: '100%' }}
                  disabled={!filterCategory}
                >
                  {subjects.map(subj => (
                    <Option key={subj.id} value={subj.id}>{subj.name}</Option>
                  ))}
                </Select>
              </Col>
              
              <Col xs={24} sm={12} md={4}>
                <Select
                  placeholder={t('Type')}
                  value={filterType}
                  onChange={setFilterType}
                  allowClear
                  style={{ width: '100%' }}
                >
                  <Option value="video_link">Video</Option>
                  <Option value="file_link">Link</Option>
                  <Option value="file_upload">PDF</Option>
                </Select>
              </Col>
              </Row>
              <Row>
              <Col xs={24} sm={12} md={6}>
                <Space>
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => {
                      setEditingResource(null);
                      form.resetFields();
                      setDrawerVisible(true);
                    }}
                  >
                    {t('Add Resource')}
                  </Button>
                  
                  <Button
                    type="dashed"
                    onClick={() => setCategoryDrawerVisible(true)}
                  >
                    {t('Add Category')}
                  </Button>
                  
                  <Button
                    type="dashed"
                    onClick={() => setSubjectDrawerVisible(true)}
                  >
                    {t('Add Subject')}
                  </Button>
                  
                  <Button
                    icon={<ReloadOutlined />}
                    onClick={fetchResources}
                  >
                    {t('Refresh')}
                  </Button>
                </Space>
              </Col>
            </Row>

            <Divider />
            
            {/* Results Summary */}
            <Text type="secondary">
              Showing {filteredResources.length} of {resources.length} resources
            </Text>
          </div>

          {/* Card Grid View */}
          <Row gutter={[16, 16]}>
            {filteredResources.map(resource => (
              <Col xs={24} sm={12} md={8} lg={6} key={resource.id}>
                <Card
                  hoverable
                  style={{ 
                    height: '100%',
                    borderRadius: 12,
                    overflow: 'hidden',
                    border: '1px solid #f0f0f0'
                  }}
                  bodyStyle={{ padding: 0 }}
                  cover={
                    resource.resource_type === 'video_link' && resource.link && getVideoThumbnail(resource.link) ? (
                      <div style={{ position: 'relative', height: 160 }}>
                        <Image
                          width="100%"
                          height={160}
                          src={getVideoThumbnail(resource.link)}
                          style={{ objectFit: 'cover' }}
                          fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAMIAAADDCAYAAADQvc6U"
                        />
                        <div style={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          background: 'rgba(0,0,0,0.7)',
                          color: 'white',
                          padding: '4px 8px',
                          borderRadius: 4,
                          fontSize: 12
                        }}>
                          <VideoCameraOutlined /> Video
                        </div>
                      </div>
                    ) : (
                      <div style={{ 
                        height: 160, 
                        backgroundColor: '#f5f5f5',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexDirection: 'column'
                      }}>
                        {getFileIcon(resource.file_path, resource.resource_type)}
                        <Text type="secondary" style={{ marginTop: 8, fontSize: 12 }}>
                          {resource.resource_type === 'file_link' ? 'External Link' :
                           resource.resource_type === 'file_upload' ? 'PDF Document' : 'Resource'}
                        </Text>
                      </div>
                    )
                  }
                  actions={[
                      resource.resource_type === 'video_link' || resource.resource_type === 'file_link' ? (
                        <Tooltip title={t('Open Link')}>
                          <Button
                            type="text"
                            icon={<EyeOutlined />}
                            onClick={() => window.open(resource.link, '_blank')}
                          >
                            {t('View')}
                          </Button>
                        </Tooltip>
                      ) : resource.file_path ? (
                        <Tooltip title={t('Download')}>
                          <Button
                            type="text"
                            icon={<DownloadOutlined />}
                            onClick={async () => {
                              try {
                                const filename = resource.file_path.split('/').pop();
                                if (!filename) {
                                  message.error(t('Invalid resource path'));
                                  return;
                                }

                                const downloadUrl = `${API_URL}/resources/download/${encodeURIComponent(filename)}`;
                                const response = await fetch(downloadUrl);

                                if (!response.ok) {
                                  throw new Error(`HTTP error! status: ${response.status}`);
                                }

                                // Force browser download in new tab
                                window.open(downloadUrl, '_blank', 'noopener,noreferrer');
                              } catch (err) {
                                console.error('❌ Error downloading resource:', err);
                                message.error(t('Failed to download resource'));
                              }
                            }}
                          >
                            {t('Download')}
                          </Button>
                        </Tooltip>
                      ) : (
                        <Button type="text" disabled>
                          {t('No File')}
                        </Button>
                      ),
                      <Tooltip title={t('Edit')}>
                        <Button
                          type="text"
                          icon={<EditOutlined />}
                          onClick={() => handleEdit(resource)}
                        >
                          {t('Edit')}
                        </Button>
                      </Tooltip>,
                      <Popconfirm
                        title={t('Are you sure you want to delete this resource?')}
                        onConfirm={() => handleDelete(resource.id)}
                        okText={t('Yes')}
                        cancelText={t('No')}
                      >
                        <Tooltip title={t('Delete')}>
                          <Button type="text" danger icon={<DeleteOutlined />}>
                            {t('Delete')}
                          </Button>
                        </Tooltip>
                      </Popconfirm>
                    ]}

                >
                  <div style={{ padding: 16 }}>
                    <Title 
                      level={5} 
                      style={{ 
                        margin: 0, 
                        marginBottom: 8,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {resource.title}
                    </Title>
                    
                    <Text 
                      type="secondary" 
                      style={{ 
                        fontSize: 12,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        lineHeight: 1.4,
                        minHeight: 34
                      }}
                    >
                      {resource.description || 'No description provided'}
                    </Text>
                    
                    <div style={{ marginTop: 12, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                      <Tag color="blue" size="small">
                        {resource.category_name || 'Unknown'}
                      </Tag>
                      <Tag color="green" size="small">
                        {resource.subject_name || 'Unknown'}
                      </Tag>
                      <Tag 
                        color={
                          resource.resource_type === 'video_link' ? 'red' :
                          resource.resource_type === 'file_link' ? 'blue' : 'cyan'
                        }
                        size="small"
                      >
                        {resource.resource_type === 'video_link' ? 'Video' :
                         resource.resource_type === 'file_link' ? 'Link' : 'PDF'}
                      </Tag>
                    </div>
                  </div>
                </Card>
              </Col>
            ))}
          </Row>

          {/* Empty State */}
          {filteredResources.length === 0 && !loading && (
            <div style={{ 
              textAlign: 'center', 
              padding: '60px 20px',
              color: '#999'
            }}>
              <FileTextOutlined style={{ fontSize: 48, marginBottom: 16 }} />
              <Title level={4} type="secondary">
                {searchText || filterCategory || filterSubject || filterType 
                  ? t('No resources match your filters') 
                  : t('No resources yet')
                }
              </Title>
              <Text type="secondary">
                {searchText || filterCategory || filterSubject || filterType
                  ? t('Try adjusting your search criteria')
                  : t('Click "Add Resource" to create your first resource')
                }
              </Text>
            </div>
          )}
        </Card>

        {/* Add/Edit Drawer */}
        <Drawer
          title={
            <Space>
              {editingResource ? <EditOutlined /> : <PlusOutlined />}
              {editingResource ? t('Edit Resource') : t('Add New Resource')}
            </Space>
          }
          width={600}
          onClose={() => {
            setDrawerVisible(false);
            form.resetFields();
            setEditingResource(null);
          }}
          open={drawerVisible}
          extra={
            <Space>
              <Button
                onClick={() => {
                  setDrawerVisible(false);
                  form.resetFields();
                  setEditingResource(null);
                }}
              >
                {t('Cancel')}
              </Button>
              <Button
                type="primary"
                onClick={() => form.submit()}
                icon={editingResource ? <EditOutlined /> : <PlusOutlined />}
              >
                {editingResource ? t('Update') : t('Create')}
              </Button>
            </Space>
          }
        >
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            initialValues={{ resource_type: 'file_upload' }}
          >
            <Form.Item
              name="category_id"
              label={t('Category')}
              rules={[{ required: true, message: t('Please select category') }]}
            >
              <Select
                placeholder={t('Select category')}
                onChange={handleCategoryChange}
                size="large"
              >
                {categories.map(cat => (
                  <Option key={cat.id} value={cat.id}>{cat.name}</Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              name="subject_id"
              label={t('Subject')}
              rules={[{ required: true, message: t('Please select subject') }]}
            >
              <Select placeholder={t('Select subject')} size="large">
                {subjects.map(subj => (
                  <Option key={subj.id} value={subj.id}>{subj.name}</Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              name="title"
              label={t('Title')}
              rules={[{ required: true, message: t('Please enter title') }]}
            >
              <Input placeholder={t('Resource title')} size="large" />
            </Form.Item>

            <Form.Item name="description" label={t('Description')}>
              <TextArea rows={4} placeholder={t('Resource description')} />
            </Form.Item>

            <Form.Item
              name="resource_type"
              label={t('Resource Type')}
              rules={[{ required: true, message: t('Please select resource type') }]}
            >
              <Select placeholder={t('Select resource type')} size="large">
                <Option value="file_upload">
                  <Space><FileTextOutlined style={{ color: '#52c41a' }} /> PDF Upload</Space>
                </Option>
                <Option value="video_link">
                  <Space><VideoCameraOutlined style={{ color: '#ff4d4f' }} /> Video Link</Space>
                </Option>
                <Option value="file_link">
                  <Space><LinkOutlined style={{ color: '#1890ff' }} /> File Link</Space>
                </Option>
              </Select>
            </Form.Item>

            <Form.Item noStyle shouldUpdate={(prevValues, currentValues) => 
              prevValues.resource_type !== currentValues.resource_type
            }>
              {({ getFieldValue }) => {
                const resourceType = getFieldValue('resource_type');
                
                if (resourceType === 'file_upload') {
                  return (
                    <Form.Item
                      name="pdfFile"
                      label={t('PDF File')}
                      rules={!editingResource ? [{ required: true, message: t('Please upload PDF file') }] : []}
                    >
                      <Upload.Dragger
                        accept=".pdf"
                        maxCount={1}
                        beforeUpload={() => false}
                        style={{ 
                          backgroundColor: '#fafafa',
                          border: '2px dashed #d9d9d9'
                        }}
                      >
                        <p className="ant-upload-drag-icon">
                          <UploadOutlined style={{ fontSize: 48, color: '#1890ff' }} />
                        </p>
                        <p className="ant-upload-text">{t('Click or drag PDF file to upload')}</p>
                        <p className="ant-upload-hint">{t('Support for single PDF file up to 10MB')}</p>
                      </Upload.Dragger>
                    </Form.Item>
                  );
                }
                
                if (resourceType === 'video_link' || resourceType === 'file_link') {
                  return (
                    <Form.Item
                      name="link"
                      label={resourceType === 'video_link' ? t('Video URL') : t('File URL')}
                      rules={[
                        { required: true, message: t('Please enter URL') },
                        { type: 'url', message: t('Please enter valid URL') }
                      ]}
                    >
                      <Input 
                        placeholder={
                          resourceType === 'video_link' 
                            ? 'https://youtube.com/watch?v=... or https://vimeo.com/...' 
                            : 'https://example.com/file.pdf'
                        }
                        size="large"
                        prefix={
                          resourceType === 'video_link' 
                            ? <VideoCameraOutlined style={{ color: '#ff4d4f' }} />
                            : <LinkOutlined style={{ color: '#1890ff' }} />
                        }
                      />
                    </Form.Item>
                  );
                }
                
                return null;
              }}
            </Form.Item>

            {/* Preview Section */}
            <Form.Item noStyle shouldUpdate={(prevValues, currentValues) => 
              prevValues.link !== currentValues.link || prevValues.resource_type !== currentValues.resource_type
            }>
              {({ getFieldValue }) => {
                const resourceType = getFieldValue('resource_type');
                const link = getFieldValue('link');
                
                if (resourceType === 'video_link' && link) {
                  const thumbnail = getVideoThumbnail(link);
                  if (thumbnail) {
                    return (
                      <div style={{ 
                        marginBottom: 16, 
                        padding: 12, 
                        backgroundColor: '#f6ffed', 
                        borderRadius: 6,
                        border: '1px solid #b7eb8f'
                      }}>
                        <Text strong style={{ color: '#52c41a', marginBottom: 8, display: 'block' }}>
                          {t('Video Preview')}:
                        </Text>
                        <Image
                          width={120}
                          height={90}
                          src={thumbnail}
                          style={{ borderRadius: 4 }}
                          fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAMIAAADDCAYAAADQvc6UAAABRWlDQ1BJQ0MgUHJvZmlsZQAAKJFjYGASSSwoyGFhYGDIzSspCnJ3UoiIjFJgf8LAwSDCIMogwMCcmFxc4BgQ4ANUwgCjUcG3awyMIPqyLsis7PPOq3QdDFcvjV3jOD1boQVTPQrgSkktTgbSf4A4LbmgqISBgTEFyFYuLykAsTuAbJEioKOA7DkgdjqEvQHEToKwj4DVhAQ5A9k3gGyB5IxEoBmML4BsnSQk8XQkNtReEOBxcfXxUQg1Mjc0dyHgXNJBSWpFCYh2zi+oLMpMzyhRcASGUqqCZ16yno6CkYGRAQMDKMwhqj/fAIcloxgHQqxAjIHBEugw5sUIsSQpBobtQPdLciLEVJYzMPBHMDBsayhILEqEO4DxG0txmrERhM29nYGBddr//5/DGRjYNRkY/l7////39v///y4Dmn+LgeHANwDrkl1AuO+pmgAAADhlWElmTU0AKgAAAAgAAYdpAAQAAAABAAAAGgAAAAAAAqACAAQAAAABAAAAwqADAAQAAAABAAAAwwAAAAD9b/HnAAAHlklEQVR4Ae3dP3Ik1RnG4W+FgYxN"
                        />
                      </div>
                    );
                  }
                }
                return null;
              }}
            </Form.Item>
          </Form>
        </Drawer>

        {/* Category Management Drawer */}
        <Drawer
          title={
            <Space>
              {t('Add New Category')}
            </Space>
          }
          width={600}
          onClose={() => {
            setCategoryDrawerVisible(false);
            categoryForm.resetFields();
          }}
          open={categoryDrawerVisible}
          extra={
            <Space>
              <Button
                onClick={() => {
                  setCategoryDrawerVisible(false);
                  categoryForm.resetFields();
                }}
              >
                {t('Cancel')}
              </Button>
              <Button
                type="primary"
                onClick={() => categoryForm.submit()}
                icon={<PlusOutlined />}
              >
                {t('Create Category')}
              </Button>
            </Space>
          }
        >
          <Form
            form={categoryForm}
            layout="vertical"
            onFinish={handleCreateCategory}
          >
            <Form.Item
              name="name"
              label={t('Category Name')}
              rules={[
                { required: true, message: t('Please enter category name') },
                { min: 2, message: t('Category name must be at least 2 characters') }
              ]}
            >
              <Input 
                placeholder={t('e.g., Mathematics, Science, English')} 
                size="large"
              />
            </Form.Item>
            
            <div style={{ 
              padding: '16px',
              backgroundColor: '#f6ffed',
              borderRadius: '6px',
              border: '1px solid #b7eb8f',
              marginBottom: '16px'
            }}>
              <Text strong style={{ color: '#52c41a' }}>
                {t('Existing Categories')}:
              </Text>
              <div style={{ marginTop: '8px' }}>
                {categories.length > 0 ? (
                  categories.map(cat => (
                    <Tag key={cat.id} color="blue" style={{ margin: '2px' }}>
                      {cat.name}
                    </Tag>
                  ))
                ) : (
                  <Text type="secondary">{t('No categories yet')}</Text>
                )}
              </div>
            </div>
          </Form>
        </Drawer>

        {/* Subject Management Drawer */}
        <Drawer
  title={
    <Space>
      
      {t('Add New Subject')}
    </Space>
  }
  width={600}
  onClose={() => {
    setSubjectDrawerVisible(false);
    subjectForm.resetFields();
  }}
  open={subjectDrawerVisible}
  extra={
    <Space>
      <Button
        onClick={() => {
          setSubjectDrawerVisible(false);
          subjectForm.resetFields();
        }}
      >
        {t('Cancel')}
      </Button>
      <Button
        type="primary"
        onClick={() => subjectForm.submit()}
        icon={<PlusOutlined />}
      >
        {t('Create Subject')}
      </Button>
    </Space>
  }
>
  <Form
    form={subjectForm}
    layout="vertical"
    onFinish={handleCreateSubject}
  >
    <Form.Item
      name="name"
      label={t('Subject Name')}
      rules={[
        { required: true, message: t('Please enter subject name') },
        { min: 2, message: t('Subject name must be at least 2 characters') }
      ]}
    >
      <Input 
        placeholder={t('e.g., Algebra, Biology, Grammar')} 
        size="large"
      />
    </Form.Item>

    <div style={{ 
      padding: '16px',
      backgroundColor: '#fff7e6',
      borderRadius: '6px',
      border: '1px solid #ffd591',
      marginBottom: '16px'
    }}>
      <Text strong style={{ color: '#fa8c16' }}>
        {t('Existing Subjects')}:
      </Text>
      <div style={{ marginTop: '8px' }}>
        {subjects.length > 0 ? (
          subjects.map(subj => (
            <Tag key={subj.id} color="orange" style={{ margin: '2px' }}>
              {subj.name}
            </Tag>
          ))
        ) : (
          <Text type="secondary">{t('No subjects yet')}</Text>
        )}
      </div>
    </div>
  </Form>
</Drawer>

      </div>

      <style jsx>{`
        @media (max-width: 991px) {
          .d-none { display: none !important; }
          .d-block { display: block !important; }
        }
        @media (min-width: 992px) {
          .d-lg-none { display: none !important; }
          .d-lg-block { display: block !important; }
        }
      `}</style>
    </div>
  );
};

export default withRoleAccess(ResourceManagement, ['admin', 'superadmin']);